import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import usePermission from 'hooks/permission/usePermission';

import { getCapitalize, getNames } from 'utils/common';

import {
    Avatar,
    Box,
    ButtonBase,
    Chip,
    ClickAwayListener,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';

import ProfileTab from './ProfileTab';
import { useAppContext } from 'store/AppProvider';
import Transitions from 'ui-elements/@extended/Transitions';
import MainCard from 'ui-elements/MainCard';

interface TabPanelProps {
    children?: ReactNode;
    value: number;
    index: number;
    dir?: string;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
        <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
            {value === index && children}
        </div>
    );
};
// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
    const { appState, logOut } = useAppContext();
    const theme = useTheme();
    const userEmail = appState.user.user?.email ?? null;
    const projectList = appState.generalData.projectsList;

    const { availableRoles, projects } = usePermission();

    const handleLogout = () => {
        logOut();
        localStorage.clear();
    };

    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current?.contains(event.target as Node)) {
            return;
        }
        setOpen(false);
    };

    const [value] = useState(0);

    const iconBackColorOpen = 'grey.300';

    const getProjectColor = (projectName: string) => projectList.find((project) => projectName === project.system_name.toString())?.color;

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }} data-locator="openProfileButton">
            <ButtonBase
                sx={{
                    p: 0.25,
                    bgcolor: open ? iconBackColorOpen : 'transparent',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'secondary.lighter' },
                }}
                aria-label="open profile"
                ref={anchorRef}
                aria-controls={open ? 'profile-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
            >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
                    <Avatar>{getNames(userEmail).name[0] + getNames(userEmail).surName[0]}</Avatar>
                    <Typography variant="body1">{userEmail}</Typography>
                </Stack>
            </ButtonBase>
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 9],
                            },
                        },
                    ],
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions type="fade" in={open} {...TransitionProps}>
                        {open && (
                            <Paper
                                data-locator="profileModal"
                                sx={{
                                    boxShadow: theme.shadows[4],
                                    width: 290,
                                    minWidth: 240,
                                    maxWidth: 290,
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: 250,
                                    },
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard elevation={0} border={false} content={false}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                padding: '8px 16px',
                                                gap: '12px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Avatar>{getNames(userEmail).name[0] + getNames(userEmail).surName[0]}</Avatar>
                                            <Typography variant="body1">
                                                {getNames(userEmail).name + ' ' + getNames(userEmail).surName}
                                            </Typography>
                                        </Box>
                                        <hr style={{ margin: '0' }} />
                                        <MenuList>
                                            <MenuItem sx={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
                                                Role:
                                                <Typography sx={{ whiteSpace: 'normal' }} color="textSecondary">
                                                    {availableRoles.map((item, index) =>
                                                        getCapitalize(index === availableRoles.length - 1 ? item : item + ', ')
                                                    )}
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem sx={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
                                                Projects:
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {projects.map((item: string) => (
                                                        <Chip
                                                            key={item}
                                                            variant="outlined"
                                                            style={{ backgroundColor: getProjectColor(item), borderRadius: '25px' }}
                                                            label={item}
                                                        />
                                                    ))}
                                                </Box>
                                            </MenuItem>
                                            <hr />
                                            <TabPanel value={value} index={0} dir={theme.direction}>
                                                <ProfileTab handleLogout={handleLogout} />
                                            </TabPanel>
                                        </MenuList>
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        )}
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default Profile;
